// Web Audio amplitude probe. The currently-playing TTS audio element is
// fed through an AnalyserNode; React components subscribe to amplitude
// either via a persistent listener (set once by useVoice) or by polling
// getCurrentAmplitude() inside their own RAF (used by DayDetail).
//
// iOS notes:
// - AudioContext starts suspended; resume() must run inside a user
//   gesture. The voice-mode entry and the Listen button qualify.
// - createMediaElementSource() may only be called once per Audio
//   element. attachAmplitude is idempotent for the same element — a
//   second call from a different layer (e.g. useVoice after
//   speakWithElevenLabs already attached) is a no-op instead of an
//   exception that would tear down the working chain.

let audioContext = null;
let analyser = null;
let source = null;
let currentAudio = null;
let rafId = null;
let onAmplitudeListener = null;
let smoothedAmplitude = 0;

function tick() {
  if (!analyser) return;
  const data = new Uint8Array(analyser.frequencyBinCount);
  analyser.getByteFrequencyData(data);
  const sum = data.reduce((a, b) => a + b * b, 0);
  const raw = Math.min(Math.sqrt(sum / data.length) / 128, 1);

  // IIR smoothing — keeps the value from jumping frame to frame, which
  // was producing visibly choppy logo pulses on iOS where the analyser
  // bursts arrive unevenly.
  smoothedAmplitude = smoothedAmplitude * 0.75 + raw * 0.25;

  if (onAmplitudeListener) onAmplitudeListener(smoothedAmplitude);
  rafId = requestAnimationFrame(tick);
}

// Persistent subscriber. Set once by useVoice on mount so any audio
// playback path — useVoice.speak, speakAndWait calling speakWithElevenLabs
// directly, etc. — drives the same React state.
export function setAmplitudeListener(fn) {
  onAmplitudeListener = typeof fn === 'function' ? fn : null;
}

// Create and resume the AudioContext SYNCHRONOUSLY inside a user gesture.
// iOS Safari only honors resume() when called as part of a user-initiated
// click/touch handler. By the time speakWithElevenLabs has fetched audio
// and is ready to play, multiple awaits have broken the gesture window —
// so callers must invoke this at the very top of their click handler
// (voice-mode entry, "Listen" button) to unlock the context up front.
export function warmupAudioContext() {
  try {
    if (!audioContext || audioContext.state === 'closed') {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      audioContext = new AC();
    }
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }
  } catch {
    // Best effort — if it fails, attachAmplitude will skip rather than
    // break audio playback.
  }
}

export function attachAmplitude(audioElement, onAmplitude) {
  try {
    if (typeof onAmplitude === 'function') {
      onAmplitudeListener = onAmplitude;
    }

    // Idempotent — same element, analyser already alive. Just make sure
    // the RAF is running so the listener keeps firing.
    if (audioElement === currentAudio && analyser) {
      if (!rafId) tick();
      return;
    }

    // (Re)create the AudioContext if needed and try to resume.
    if (!audioContext || audioContext.state === 'closed') {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      audioContext = new AC();
    }
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    // CRITICAL: if the context isn't running, don't call
    // createMediaElementSource. Doing so would reroute the audio element's
    // output to a suspended AudioContext destination — the audio would be
    // silent. Better to lose amplitude than break playback. The caller
    // should have invoked warmupAudioContext() inside the user gesture.
    if (audioContext.state !== 'running') {
      console.warn(
        '[audioAmplitude] context not running, skipping attach (audio will play without analyser)'
      );
      return;
    }

    // Tear down any prior chain before wiring the new element.
    if (source) {
      try {
        source.disconnect();
      } catch {}
      source = null;
    }

    analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    source = audioContext.createMediaElementSource(audioElement);
    source.connect(analyser);
    analyser.connect(audioContext.destination);
    currentAudio = audioElement;

    if (rafId) cancelAnimationFrame(rafId);
    tick();
  } catch (err) {
    console.warn('[audioAmplitude] attach failed:', err?.message || err);
  }
}

export function detachAmplitude() {
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  if (source) {
    try {
      source.disconnect();
    } catch {}
    source = null;
  }
  analyser = null;
  currentAudio = null;
  smoothedAmplitude = 0;
  // onAmplitudeListener intentionally NOT cleared so a subsequent attach
  // (next chunk in a sequence, or a direct speakWithElevenLabs call) keeps
  // driving the same setter without re-registration.
}

export function getCurrentAmplitude() {
  // Return the smoothed value cached by tick() so polling consumers
  // (DayDetail) get the same anti-jitter smoothing as listener consumers.
  return analyser ? smoothedAmplitude : 0;
}
